/* mw-masonry — masonry layout and «Show more» for blocks/masonry.liquid. Light DOM.

   Markup (rendered by the block):
   <mw-masonry class="blk masonry [masonry--limit] [masonry--limit-m]" style="--mas-cols…">
     <div class="masonry__items" id="…">…nested blocks…</div>
     [<div class="masonry__more" hidden><button class="masonry__toggle" aria-expanded="false"
        aria-controls="…" data-label-more="…" data-label-less="…">…</button></div>]
   </mw-masonry>

   What the script adds (the server markup is a CSS-columns layout without it):
   - .is-ready: the list becomes a grid of 1px rows (masonry.css). Every block goes, in order,
     under the shortest column (inline grid-column / grid-row spanning its height), and the
     vertical gap is added by hand because the rows are 1px, as Helix does with translate().
     Re-run on resize, on image loads (ResizeObserver) and when blocks change.
   - .is-limited when the blocks are taller than the limit for this device (--_limit, 0 = none):
     the list is cut with a fade and the button shows. Blocks below the cut get `inert`, so they
     are out of the tab order until the cut is opened (Helix does the same).
   - Open = max-height animates to the full height (masonry.css), then lets go; closing animates
     back and returns the page to where the button was.
   - Theme editor: selecting a block inside opens the cut so the block can be seen. */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class MwMasonry extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector(':scope > .masonry__items');
    if (!this.list) return;
    this.more = this.querySelector(':scope > .masonry__more');
    this.toggle = this.more?.querySelector('.masonry__toggle');

    this.abort = new AbortController();
    const { signal } = this.abort;

    this.classList.add('is-ready');
    this.frame = 0;
    this.restoreY = null;

    this.resizeObserver = new ResizeObserver(() => this.schedule());
    this.observeItems();
    this.mutationObserver = new MutationObserver(() => {
      this.observeItems();
      this.schedule();
    });
    this.mutationObserver.observe(this.list, { childList: true });

    this.toggle?.addEventListener('click', () => this.setOpen(!this.open), { signal });
    this.list.addEventListener(
      'transitionend',
      (event) => {
        if (event.target === this.list && event.propertyName === 'max-height' && this.open) {
          this.style.setProperty('--mas-open', 'none');
        }
      },
      { signal }
    );
    document.addEventListener(
      'shopify:block:select',
      (event) => {
        if (event.target instanceof Node && this.list.contains(event.target)) this.setOpen(true, true);
      },
      { signal }
    );

    this.layout();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    cancelAnimationFrame(this.frame);
  }

  get items() {
    return [...this.list.children].filter((el) => el instanceof HTMLElement && el.matches('.blk, .shopify-app-block'));
  }

  get open() {
    return this.classList.contains('is-open');
  }

  /* The limit for the current viewport: masonry.css swaps --_limit at 768px. 0 = no limit. */
  get limit() {
    return Math.max(0, parseFloat(getComputedStyle(this).getPropertyValue('--_limit')) || 0);
  }

  observeItems() {
    this.resizeObserver.disconnect();
    this.resizeObserver.observe(this.list);
    this.items.forEach((item) => this.resizeObserver.observe(item));
  }

  schedule() {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.layout());
  }

  layout() {
    // The column count comes from the breakpoint variable, not from the computed grid: an item
    // still carrying `grid-column: 3` from a wider layout creates implicit tracks, the computed
    // value counts them, and the grid could never drop back to fewer columns (seen in the theme
    // editor when switching from desktop to mobile preview without a reload).
    const columns = Math.max(1, parseInt(getComputedStyle(this).getPropertyValue('--_cols'), 10) || 1);
    const gapV = Math.round(parseFloat(getComputedStyle(this).getPropertyValue('--_gap-v')) || 0);
    const heights = new Array(columns).fill(0);
    const limit = this.limit;
    const placed = [];

    this.items.forEach((item) => {
      if (getComputedStyle(item).display === 'none') return;
      const height = Math.ceil(item.getBoundingClientRect().height);
      let column = 0;
      for (let i = 1; i < columns; i++) if (heights[i] < heights[column]) column = i;
      const top = heights[column];
      const area = `${top + 1} / span ${Math.max(1, height)}`;
      if (item.style.gridRow !== area) item.style.gridRow = area;
      if (item.style.gridColumn !== String(column + 1)) item.style.gridColumn = String(column + 1);
      heights[column] = top + height + gapV;
      placed.push({ item, top, height });
    });

    const total = Math.max(0, Math.max(...heights, 0) - gapV);
    const limited = limit > 0 && total > limit + 1;

    // One column (phones): end the cut after the last whole block. A cut through the next block
    // left only its first line under the fade — on the reviews wall five grey stars on an empty
    // card, which read as a zero-star review. Several columns are staggered, so they keep the line.
    let cutAt = limit;
    if (limited && columns === 1) {
      const whole = placed.filter(({ top, height }) => top + height <= limit);
      const end = whole.length ? whole[whole.length - 1].top + whole[whole.length - 1].height : 0;
      if (end >= limit * 0.5) cutAt = end;
    }
    if (cutAt !== limit) this.style.setProperty('--mas-snap', `${cutAt}px`);
    else this.style.removeProperty('--mas-snap');

    this.classList.toggle('is-limited', limited);
    if (this.more) this.more.hidden = !limited;
    if (!limited && this.open) this.classList.remove('is-open');

    // Blocks below the cut are out of the tab order until it is opened.
    const cut = limited && !this.open;
    placed.forEach(({ item, top, height }) => {
      const hidden = cut && top + height / 2 > cutAt;
      if (item.inert !== hidden) item.inert = hidden;
    });

    if (this.open && limited && this.style.getPropertyValue('--mas-open') !== 'none') {
      this.style.setProperty('--mas-open', `${total}px`);
    }
  }

  setOpen(open, instant = false) {
    if (!this.toggle || open === this.open) return;
    const animate = !instant && !reducedMotion.matches;

    if (open) {
      // The height has to be a length *before* .is-open applies, or max-height goes straight to
      // `none` and there is nothing to animate from (measured on the theme 20.09).
      this.restoreY = window.scrollY;
      this.style.setProperty('--mas-open', animate ? `${this.list.scrollHeight}px` : 'none');
      this.classList.add('is-open');
      this.layout();
    } else {
      // From the real height back to the limit: pin the current height first so it can animate.
      this.style.setProperty('--mas-open', `${this.list.offsetHeight}px`);
      void this.list.offsetHeight;
      this.classList.remove('is-open');
      this.style.removeProperty('--mas-open');
      this.layout();
      if (this.restoreY !== null) {
        window.scrollTo({ top: this.restoreY, behavior: animate ? 'smooth' : 'auto' });
        this.restoreY = null;
      }
    }

    this.toggle.setAttribute('aria-expanded', String(open));
    this.toggle.textContent = open ? this.toggle.dataset.labelLess : this.toggle.dataset.labelMore;
  }
}

if (!customElements.get('mw-masonry')) customElements.define('mw-masonry', MwMasonry);

export { MwMasonry };
